import React, { memo, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { CancelToken, isCancel } from 'axios';

import CurrentUserContext from '../../Context/CurrentUserContext';
import AuthService from '../../Services/AuthService';
import SchedulesService from '../../Services/SchedulesService';
import ServiceCompleteModalDisplay from './ServiceCompleteModalDisplay';

export default memo(ServiceCompleteModalContainer);

ServiceCompleteModalContainer.propTypes = {
  acknowledgementQueue: PropTypes.object,
  assetShadowSignalsMap: PropTypes.object,
  isOpen: PropTypes.bool,
  refreshState: PropTypes.func,
  selectedCount: PropTypes.number,
  toggleModal: PropTypes.func,
};
ServiceCompleteModalContainer.defaultProps = {
  acknowledgementQueue: new Map(),
  isOpen: false,
  refreshState: () => {},
  selectedCount: 0,
  toggleModal: () => {},
};

function ServiceCompleteModalContainer(props) {
  const [resetReminder, toggleResetReminder] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const { _id: currentUserID } = useContext(CurrentUserContext);
  const now = moment();

  const authService = new AuthService();
  const schedulesService = new SchedulesService();
  const source = CancelToken.source();

  const handleCompleteMaintenance = async () => {
    setProcessing(true);
    try {
      await handleApiRequests(props.acknowledgementQueue);

      setProcessing('complete');
      await props.refreshState({}, true);
    } catch (err) {
      if (isCancel(err)) return;
      const { message } = err.response?.data ?? err;
      setError(message);
      setProcessing(false);
    }
  };

  const buildUpdateRequest = (instance) => {
    const {
      id,
      reminder: { trigger: { name, type } = {} } = {},
      value,
    } = instance;

    const { value: signalValue = 0 } =
      props.assetShadowSignalsMap.get(name) ?? {};
    const offset = Math.round(signalValue) - Math.round(value);
    const lastRun = new Date().toISOString();

    const data = type === 'conditional' ? { offset } : { lastRun };

    return schedulesService.editReminderInstance({
      orgID: authService.getUserRootOrgID(),
      instanceId: id,
      data,
      cancelToken: source.token,
    });
  };

  const handleApiRequests = (acknowledgementQueue = new Map()) => {
    if (!acknowledgementQueue.size) return Promise.resolve();

    const { completeRequestData, updateReminderRequests } = [
      ...acknowledgementQueue.entries(),
    ].reduce(
      (reqs, [triggerID, instance]) => {
        const { name } = instance?.reminder?.trigger ?? {};
        const { value: signalValue } =
          props.assetShadowSignalsMap.get(name) ?? {};
        const serviceDate = now.clone().utc().format();

        const body = {
          serviceDate,
          signalValue,
          userId: currentUserID,
          reminderTriggerId: triggerID,
          reminderInstanceId: instance.id,
        };
        reqs.completeRequestData.push(body);

        if (resetReminder) {
          reqs.updateReminderRequests.push(buildUpdateRequest(instance));
        }

        return reqs;
      },
      { completeRequestData: [], updateReminderRequests: [] }
    );
    const completeServiceRequest = schedulesService.addMaintenanceLogBulk({
      data: completeRequestData,
      orgID: authService.getUserRootOrgID(),
      cancelToken: source.token,
    });

    return Promise.all([completeServiceRequest, ...updateReminderRequests]);
  };

  useEffect(() => source.cancel, []);

  return (
    <ServiceCompleteModalDisplay
      {...props}
      error={error}
      handleCompleteMaintenance={handleCompleteMaintenance}
      now={now}
      processing={processing}
      resetReminder={resetReminder}
      toggleResetReminder={toggleResetReminder}
    />
  );
}

// Exports to ./ServiceTableDisplay
